import { ENQUIRY_NO_REPLY_AFTER, ENQUIRY_SECOND_AFTER_DAYS } from './enquiry-message'
import { leadTone } from './new-jobs'
import type { Lead } from './types'

// Where an enquiry stands with its customer (New jobs redesign, Liam 10 Oct 2026, drawing A:
// https://claude.ai/artifact/FwSzgYQbf125GgPaqczJWh). Pure, so New jobs, Messages and the checks read one rule:
//   - To answer: nobody has replied to them yet, or they have written back since our last message;
//   - Waiting on them: we sent the last message (a reply, a quote, a nudge) and it is too soon to nudge;
//   - To chase: one message out, no answer, and the nudge is due;
//   - Let go: two messages out and no answer a week after the second ("maybe after second message we could let it
//     go"); it sits with Maybe later, and nothing stored changes, so a reply or a new message brings it straight back.
// The message count is the lead's own (enquiry_messages, migration 0012): every reply, quote or nudge sent from
// Roundly adds one, and "They replied" puts it back to 0.

// The nudge waits ENQUIRY_SECOND_AFTER_DAYS (5) and two messages with no reply is the take-off count
// (ENQUIRY_NO_REPLY_AFTER), both from enquiry-message.ts, the rule Messages has always used.
// How long after the second message an enquiry with no answer is let go.
export const LET_GO_AFTER_DAYS = 7

export type EnquiryLane = 'answer' | 'waiting' | 'chase' | 'letgo' | 'later' | 'won' | 'closed' | 'sorting'

export function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000)
}

type LaneLead = Pick<Lead, 'status' | 'status_raw'> & Partial<Pick<Lead, 'enquiry_messages' | 'enquiry_messaged_on' | 'replies_unseen'>>

export function messagesSent(l: Pick<Partial<Lead>, 'enquiry_messages'>): number {
  return Math.max(0, Math.round(Number(l.enquiry_messages) || 0))
}

// The day the nudge is due (one message out), or null when no message is out.
export function nudgeDueOn(l: Pick<Partial<Lead>, 'enquiry_messaged_on'>): string | null {
  return l.enquiry_messaged_on ? addDaysISO(l.enquiry_messaged_on, ENQUIRY_SECOND_AFTER_DAYS) : null
}

// The day an enquiry messaged twice with no answer is let go, or null while fewer than two messages are out.
export function letGoOn(l: Pick<Partial<Lead>, 'enquiry_messages' | 'enquiry_messaged_on'>): string | null {
  return messagesSent(l) >= ENQUIRY_NO_REPLY_AFTER && l.enquiry_messaged_on ? addDaysISO(l.enquiry_messaged_on, LET_GO_AFTER_DAYS) : null
}

export function enquiryLane(l: LaneLead, today: string): EnquiryLane {
  const tone = leadTone(l)
  if (tone === 'confirmed') return 'won'
  if (tone === 'declined' || tone === 'cancelled') return 'closed'
  if (tone === 'maybe') return 'later'
  // They wrote back (an emailed reply not yet seen): it is our turn, whatever we sent before.
  if ((l.replies_unseen ?? 0) > 0) return 'answer'
  const n = messagesSent(l)
  if (n === 0) {
    // Quoted outside this count (the sheet's imported quotes, or a status picked by hand): waiting on them.
    if (tone === 'quote') return 'waiting'
    // Wording that fits no lane ("NOT HOME", anything typed) waits to be sorted, never hidden.
    if ((l.status_raw ?? '').trim()) return 'sorting'
    return 'answer'
  }
  if (n === 1) {
    const due = nudgeDueOn(l)
    return due && today >= due ? 'chase' : 'waiting'
  }
  const goes = letGoOn(l)
  return goes && today >= goes ? 'letgo' : 'waiting'
}

// The customer's own words from an emailed enquiry ("Subject: …", a blank line, their message, then for a website
// form the details the reader added: "Postcode: …", "Area: …", "Property: …", "Services: …", "From: …"). An enquiry
// typed in by hand or brought from the sheet keeps its note as it is: the sheet's "Initial lead" column says how they
// found the business, so it is shown as that, not as a message.
export interface EnquiryWords { message: string | null; details: [string, string][]; note: string | null }
const DETAIL_KEYS = new Set(['Postcode', 'Area', 'Property', 'Services', 'From'])
export function enquiryWords(l: Pick<Lead, 'initial_lead'> & Partial<Pick<Lead, 'came_by'>>): EnquiryWords {
  const raw = (l.initial_lead ?? '').replace(/\r\n/g, '\n').trim()
  if (!raw) return { message: null, details: [], note: null }
  if (l.came_by !== 'email' || !raw.startsWith('Subject:')) return { message: null, details: [], note: raw }
  let body = raw.replace(/^Subject:[^\n]*\n*/, '').trim()
  const details: [string, string][] = []
  const blocks = body.split(/\n\s*\n/)
  const last = blocks.at(-1) ?? ''
  const lines = last.split('\n').map((s) => s.trim()).filter(Boolean)
  const pairs = lines.map((s) => /^([A-Z][A-Za-z]+): (.+)$/.exec(s))
  if (blocks.length > 1 && lines.length && pairs.every((m) => m && DETAIL_KEYS.has(m[1]!))) {
    for (const m of pairs) details.push([m![1]!, m![2]!])
    body = blocks.slice(0, -1).join('\n\n').trim()
  }
  const message = body && body !== '(No message, only the form)' ? body : null
  return { message, details, note: null }
}

// The first lines of their message for a list row: whole words, an ellipsis when cut.
export function messageExcerpt(message: string | null, max = 110): string | null {
  if (!message) return null
  const flat = message.replace(/\s+/g, ' ').trim()
  if (flat.length <= max) return flat
  const cut = flat.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:!?-]+$/, '')}…`
}
